/**
 * oRPC typed client for the Electric Cloud API.
 *
 * Uses the admin-api-contract for compile-time type safety.
 * When the API contract changes, this CLI fails to build if not updated.
 */

import { createORPCClient } from '@orpc/client'
import { RPCLink } from '@orpc/client/fetch'
import type { ContractRouterClient } from '@orpc/contract'
import type { Contract } from 'admin-api-contract'
import { resolveAuth } from './auth.js'

declare const __CLI_VERSION__: string
export const CLI_VERSION =
  typeof __CLI_VERSION__ !== `undefined` ? __CLI_VERSION__ : `0.0.1`

let _verbose = false

export function setVerbose(value: boolean): void {
  _verbose = value
}

export function getApiBaseUrl(): string {
  return process.env.ELECTRIC_API_URL ?? `https://dashboard.electric-sql.cloud`
}

export function getDashboardUrl(): string {
  return (
    process.env.ELECTRIC_DASHBOARD_URL ?? `https://dashboard.electric-sql.cloud`
  )
}

export function createClient(): ContractRouterClient<Contract> {
  const link = new RPCLink({
    url: `${getApiBaseUrl()}/api/rpc`,
    headers: async () => {
      const headers: Record<string, string> = {
        'User-Agent': `electric-cli/${CLI_VERSION}`,
      }
      const auth = await resolveAuth()
      headers[`Authorization`] = `Bearer ${auth.token}`
      return headers
    },
    fetch: _verbose ? verboseFetch : undefined,
  })
  return createORPCClient(link)
}

export function createPublicClient(): ContractRouterClient<Contract> {
  const link = new RPCLink({
    url: `${getApiBaseUrl()}/api/rpc`,
    headers: () => ({
      'User-Agent': `electric-cli/${CLI_VERSION}`,
    }),
    fetch: _verbose ? verboseFetch : undefined,
  })
  return createORPCClient(link)
}

async function verboseFetch(
  request: Request,
  init: { redirect?: Request[`redirect`] }
): Promise<Response> {
  const method = request.method
  const url = request.url
  const start = performance.now()

  process.stderr.write(`${method} ${url}\n`)

  const response = await fetch(request, init)

  const elapsed = Math.round(performance.now() - start)
  process.stderr.write(
    `${response.status} ${response.statusText} (${elapsed}ms)\n`
  )

  const requestId = response.headers.get(`x-request-id`)
  if (requestId) {
    process.stderr.write(`X-Request-Id: ${requestId}\n`)
  }

  return response
}
